import { extraQueryParams } from '@utils/constants';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import {
  addFavouriteAlbum,
  addFavouriteCompanion,
  addFavouriteContributor,
  addFavouriteTrack,
  removeFavouriteCompanion as removeFavouriteCompanionAPI,
  removeFavouriteAlbum as removeFavouriteAlbumAPI,
  removeFavouriteContributor as removeFavouriteContributorAPI,
  removeFavouriteTrack as removeFavouriteTrackAPI,
} from '../services/api';
import { analytics } from '../utils/analytics';
import { getContentType } from '../utils/contentType';
import { showToast } from '../utils/toast';
import { useMusicState } from './storeHooks/useMusicState';

/**
 * Track favorite event to analytics
 * @param id - Content ID
 * @param type - Content type ('album', 'track', or 'contributor')
 * @param action - Action performed ('Added' or 'Removed')
 * @param contentName - Name of the content
 * @param creatorName - Name of the creator/contributor
 * @param category - Category for determining content type
 */
const trackFavoriteEvent = (
  id: string,
  type: 'album' | 'track' | 'contributor' | 'companion',
  action: 'Added' | 'Removed',
  contentName?: string,
  creatorName?: string,
  category?: string,
) => {
  try {
    if (!contentName) {
      console.warn(
        `Analytics: Missing content name for ${type} ${id}, using fallback`,
      );
    }
    if (!creatorName) {
      console.warn(
        `Analytics: Missing creator name for ${type} ${id}, using fallback`,
      );
    }

    analytics.logEvent('content_favorited', {
      content_id: id,
      content_type:
        type === 'contributor'
          ? 'Creator'
          : type === 'companion'
            ? 'Companion'
            : getContentType(category),
      content_name: contentName || 'Unknown',
      creator_name: creatorName || 'Unknown Artist',
      action,
    });
  } catch (error) {
    console.error('Analytics tracking failed:', error);
    // Don't break the favorite functionality if analytics fails
  }
};

/**
 * Custom hook for managing favorites (albums, tracks, contributors)
 * Provides a simple toggleFavorite function that handles both adding and removing favorites
 */
export const useFavorites = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const {
    musicforyouList,
    serenityPlaylist,
    wisdomPlaylist,
    updateAlbumLikedStatus,
    updateTrackLikedStatus,
    addFavouriteAlbumToStore,
    addFavouriteTrackToStore,
    addFavouriteContributorToStore,
    removeFavouriteAlbum,
    removeFavouriteTrack,
    removeFavouriteContributor,
    favouriteContributors,
  } = useMusicState();

  /**
   * Toggle favorite status for an album, track, or contributor
   * @param type - The type of item ('album', 'track', 'contributor', or 'companion')
   * @param id - The ID of the item
   * @param isLiked - Current liked status
   * @param contentName - Name of the content for analytics
   * @param creatorName - Creator/contributor name for analytics
   * @param category - Category for determining content type
   */
  const toggleFavorite = useCallback(
    async (
      type: 'album' | 'track' | 'contributor' | 'companion',
      id: string,
      isLiked?: boolean,
      contentName?: string,
      creatorName?: string,
      category?: string,
      slug?: string,
    ) => {
      // Check if user is authenticated
      if (!auth.isAuthenticated) {
        console.log('User not authenticated, redirecting to login...');
        const fullPath = window.location.pathname + window.location.search;
        auth
          .signinRedirect({
            extraQueryParams,
            state: { returnUrl: fullPath },
          })
          .catch((error) => {
            console.error('Error during sign-in redirect:', error);
          });
        return;
      }

      // Find the item to determine current liked status
      const isCurrentlyLiked = isLiked || false;
      const albumName = '';

      try {
        // Optimistic update - update UI immediately
        if (type === 'album') {
          updateAlbumLikedStatus(id, !isCurrentlyLiked);
        } else if (type === 'track') {
          updateTrackLikedStatus(id, !isCurrentlyLiked);
        }

        // Perform API call based on current status
        if (isCurrentlyLiked) {
          // Remove from favorites
          if (type === 'album') {
            showToast.unliked(albumName);
            await removeFavouriteAlbumAPI(id);
            removeFavouriteAlbum(id);
            trackFavoriteEvent(
              id,
              type,
              'Removed',
              contentName,
              creatorName,
              category,
            );
          } else if (type === 'contributor') {
            showToast.unliked();
            await removeFavouriteContributorAPI(id);
            removeFavouriteContributor(id);
            trackFavoriteEvent(
              id,
              type,
              'Removed',
              contentName,
              creatorName,
              category,
            );
          } else if (type === 'track') {
            showToast.unliked();
            await removeFavouriteTrackAPI(id);
            removeFavouriteTrack(id);
            trackFavoriteEvent(
              id,
              type,
              'Removed',
              contentName,
              creatorName,
              category,
            );
          } else if (type === 'companion') {
            showToast.unliked();
            await removeFavouriteCompanionAPI(id);
            trackFavoriteEvent(
              id,
              type,
              'Removed',
              contentName,
              creatorName,
              category,
            );
          }
        } else {
          // Add to favorites
          if (type === 'album') {
            showToast.liked(albumName);
            await addFavouriteAlbum(id);
            // Add to Redux store
            const album = [
              ...musicforyouList,
              ...serenityPlaylist,
              ...wisdomPlaylist,
            ].find((album) => album.id === id);
            if (album) {
              const favoriteAlbumData = {
                id: album.id,
                name: album.albumName,
                albumArtUrl: album.albumArtUrl,
                contributors: album.contributors,
                category: album.category || 'music',
                createdAt: new Date().toISOString(),
              };
              addFavouriteAlbumToStore(favoriteAlbumData);
            }
            trackFavoriteEvent(
              id,
              type,
              'Added',
              contentName,
              creatorName,
              category,
            );
          } else if (type === 'contributor') {
            showToast.liked();
            await addFavouriteContributor(id);
            // Add to Redux store
            const basicContributorData = {
              id: id,
              givenName: contentName || 'Contributor',
              createdAt: new Date().toISOString(),
            };
            addFavouriteContributorToStore(basicContributorData);
            trackFavoriteEvent(
              id,
              type,
              'Added',
              contentName,
              creatorName,
              category,
            );
          } else if (type === 'track') {
            showToast.liked();
            await addFavouriteTrack(id);
            // Add to Redux store
            const basicTrackData = {
              id: id,
              name: contentName || 'Track',
              createdAt: new Date().toISOString(),
            };
            addFavouriteTrackToStore(basicTrackData);
            trackFavoriteEvent(
              id,
              type,
              'Added',
              contentName,
              creatorName,
              category,
            );
          } else if (type === 'companion') {
            showToast.liked();
            await addFavouriteCompanion(id, slug || '');
            trackFavoriteEvent(
              id,
              type,
              'Added',
              contentName,
              creatorName,
              category,
            );
          }
        }
      } catch (err) {
        console.error('Error toggling favorite:', err);
        // Revert the optimistic update on error
        if (type === 'album') {
          updateAlbumLikedStatus(id, isCurrentlyLiked);
        } else if (type === 'track') {
          updateTrackLikedStatus(id, isCurrentlyLiked);
        }
        // Show error toast
        showToast.error(t('common.failedToUpdateFavorites'));
        throw err;
      }
    },
    [
      auth,
      musicforyouList,
      serenityPlaylist,
      wisdomPlaylist,
      updateAlbumLikedStatus,
      updateTrackLikedStatus,
      addFavouriteAlbumToStore,
      addFavouriteTrackToStore,
      addFavouriteContributorToStore,
      removeFavouriteAlbum,
      removeFavouriteTrack,
      removeFavouriteContributor,
      favouriteContributors,
    ],
  );

  return {
    toggleFavorite,
  };
};
